class LookbookSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.labels = {
      product: section.dataset.labelProduct || 'View product',
      detail: section.dataset.labelDetail || 'Read more',
      image: section.dataset.labelImage || 'View image',
      close: section.dataset.labelClose || 'Close',
      next: section.dataset.labelNext || 'Next',
      prev: section.dataset.labelPrev || 'Previous',
    };
    this.detailMap = this.getDetailMap();
    this.grid = section.querySelector('[data-lookbook-grid]');
    this.gridWrap = section.querySelector('[data-lookbook-grid-wrap]');
    this.collapseActions = section.querySelector('[data-lookbook-collapse-actions]');
    this.collapseToggle = section.querySelector('[data-lookbook-collapse-toggle]');
    this.collapseToggleText = this.collapseToggle
      ? this.collapseToggle.querySelector('.button__text') || this.collapseToggle
      : null;
    this.collapseRows = parseInt(section.dataset.lookbookCollapseRows, 10) || 0;
    this.collapseEnabled = section.dataset.lookbookCollapseEnabled === 'true';
    this.collapseLabel = section.dataset.lookbookCollapseLabel || 'Show all';
    this.collapseLessLabel = section.dataset.lookbookCollapseLessLabel || 'Show less';
    this.loadMoreButton = section.querySelector('[data-lookbook-load-more]');
    this.loadMoreLabel = this.loadMoreButton
      ? this.loadMoreButton.querySelector('[data-lookbook-label]') || this.loadMoreButton
      : null;

    if (!this.grid) return;

    this.shuffleGrid();
    this.scheduleHydration();
    this.initCollapse();
    this.bindLightbox();
    this.bindLoadMore();
  }

  // Lazy hydration (2026-09-19): the deferred tiles (~300 cards, ~4.000 DOM nodes) are only
  // built when the section approaches the viewport or the user interacts with it. Keeps the
  // work off the critical path on collection pages (LCP/INP).
  scheduleHydration() {
    this.hydrated = false;
    const run = () => {
      if (this.hydrated) return;
      this.hydrated = true;
      this.hydrateDeferred();
      // Before hydration only the first rows exist, so updateCollapseHeight() may have
      // expanded the grid and hidden the toggle. Re-apply the collapsed state now that
      // all tiles are present; a toggle click that triggered this run() flips it afterwards.
      if (this.collapseEnabled && this.collapseRows && this.gridWrap) {
        this.gridWrap.classList.add('is-collapsed');
        this.gridWrap.classList.remove('is-expanded');
        if (this.collapseActions) this.collapseActions.hidden = false;
        this.setCollapseButtonText(this.collapseLabel, false);
      }
      this.updateCollapseHeight();
    };
    this.runHydration = run;

    // Any interaction with the grid or the collapse toggle hydrates first (capture phase).
    if (this.grid) this.grid.addEventListener('click', run, true);
    if (this.collapseToggle) this.collapseToggle.addEventListener('click', run, true);
    if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', run, true);

    if (!('IntersectionObserver' in window)) {
      window.setTimeout(run, 3000);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        run();
      }
    }, { rootMargin: '800px 0px' });
    observer.observe(this.section);
  }

  // Page-weight lazy-load (2026-07-16): the section renders only the collapse-visible
  // tiles as HTML; the rest arrive as a compact JSON island. Here we clone the first
  // rendered card as a template and rebuild the remaining tiles into the (hidden,
  // collapsed) grid, so all configs stay reachable without inline HTML for every tile.
  hydrateDeferred() {
    if (!this.grid) return;
    const script = this.section.querySelector('[data-lookbook-deferred]');
    if (!script || !script.textContent) return;
    let data;
    try {
      data = JSON.parse(script.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(data) || !data.length) return;

    const template = this.grid.querySelector('[data-lookbook-item]');
    if (!template) return;

    const fragment = document.createDocumentFragment();
    data.forEach((item) => {
      if (!item || !item.f) return;
      const el = template.cloneNode(true);

      el.setAttribute('data-lookbook-entry-id', item.e != null ? String(item.e) : '');
      el.setAttribute('data-lookbook-image', item.f);
      el.setAttribute('data-lookbook-alt', item.a || '');
      if (item.p) el.setAttribute('data-lookbook-product-url', item.p);
      else el.removeAttribute('data-lookbook-product-url');
      if (item.d) el.setAttribute('data-lookbook-detail-url', item.d);
      else el.removeAttribute('data-lookbook-detail-url');

      const link = el.querySelector('.lookbook-card__link');
      if (link) link.setAttribute('href', item.u || '#');

      const figure = el.querySelector('figure');
      if (figure && item.r) {
        const pad = (100 / parseFloat(item.r)).toFixed(4);
        figure.setAttribute('style', 'padding-top:' + pad + '%');
        // Start hidden (reveal state) so the clone never shows the template's picture
        // while its own image is still loading; revealed on the img load event below.
        figure.classList.remove('lazyloaded');
        figure.classList.add('lazy-image--to-reveal');
      }

      const img = el.querySelector('img');
      if (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-sizes');
        img.setAttribute('loading', 'lazy');
        img.classList.remove('lazyloaded');
        // Drop the template's picture first: a swapped src keeps the old image painted
        // until the new one has decoded, which looked like every tile showing the same photo.
        img.removeAttribute('src');
        const reveal = () => {
          if (figure) {
            figure.classList.add('lazyloaded');
            figure.classList.remove('lazy-image--to-reveal');
          }
          img.classList.add('lazyloaded');
        };
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true });
        // Derive a grid-sized src from the full URL (swap Shopify's width param).
        const gridSrc = item.f ? item.f.replace(/([?&]width=)\d+/, '$1600') : '';
        img.setAttribute('src', gridSrc || item.f);
        img.setAttribute('alt', item.a || '');
        if (img.complete && img.naturalWidth > 0) reveal();
      }

      // The template may carry the first entry's title/meta — drop it on clones.
      const meta = el.querySelector('.lookbook-card__meta');
      if (meta) meta.remove();

      fragment.appendChild(el);
    });

    this.grid.appendChild(fragment);
  }

  getDetailMap() {
    const script = this.section.querySelector('[data-lookbook-details]');
    if (!script) return new Map();
    return this.parseDetailScript(script);
  }

  parseDetailScript(script) {
    if (!script || !script.textContent) return new Map();
    try {
      const data = JSON.parse(script.textContent);
      if (!Array.isArray(data)) return new Map();
      return new Map(
        data
          .filter((item) => item && item.id && item.html)
          .map((item) => [String(item.id), item.html]),
      );
    } catch (error) {
      return new Map();
    }
  }

  mergeDetailMapFromDoc(doc) {
    if (!doc) return;
    const script = doc.querySelector('[data-lookbook-details]');
    if (!script) return;
    const nextMap = this.parseDetailScript(script);
    if (!nextMap.size) return;
    if (!this.detailMap) {
      this.detailMap = nextMap;
      return;
    }
    nextMap.forEach((value, key) => {
      this.detailMap.set(key, value);
    });
  }

  bindLightbox() {
    this.section.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-lookbook-lightbox]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();

      if (!window.basicLightbox) return;

      const item = trigger.closest('[data-lookbook-item]');
      const items = this.getLightboxItems();
      const startIndex = items.findIndex((entry) => entry.element === item);
      this.openLightbox(items, startIndex);
    });
  }

  getLightboxItems() {
    return Array.from(this.grid.querySelectorAll('[data-lookbook-item]'))
      .map((element) => ({
        element,
        src: element.dataset.lookbookImage,
        thumbSrc: this.getLightboxThumbSrc(element),
        alt: element.dataset.lookbookAlt || '',
        productUrl: element.dataset.lookbookProductUrl || '',
        detailUrl: element.dataset.lookbookDetailUrl || '',
        detailHtml: this.getDetailHtml(element),
      }))
      .filter((item) => item.src);
  }

  getDetailHtml(element) {
    const entryId = element.dataset.lookbookEntryId;
    if (entryId && this.detailMap && this.detailMap.has(entryId)) {
      return this.detailMap.get(entryId);
    }
    const detailElement = element.querySelector('[data-lookbook-detail]');
    return detailElement ? detailElement.innerHTML : '';
  }

  openLightbox(items, startIndex) {
    if (!items.length) return;

    let currentIndex = startIndex >= 0 ? startIndex : 0;
    let lightboxLoadId = 0;
    const update = (index) => {
      const item = items[index];
      if (!item) return;

      const img = instance.element().querySelector('[data-lookbook-lightbox-image]');
      const media = instance.element().querySelector('[data-lookbook-lightbox-media]');
      const panel = instance.element().querySelector('[data-lookbook-lightbox-panel]');

      const thumbSrc = item.thumbSrc || item.src;
      const fullSrc = item.src;
      const shouldPreloadFull = fullSrc && thumbSrc && fullSrc !== thumbSrc;

      if (!img) return;

      lightboxLoadId += 1;
      const currentLoadId = String(lightboxLoadId);
      img.dataset.lookbookLoadId = currentLoadId;
      img.dataset.lookbookFullSrc = fullSrc || '';
      img.src = thumbSrc || fullSrc;
      img.alt = item.alt;
      img.classList.remove('is-zoomed');
      img.style.setProperty('--zoom-x', '0px');
      img.style.setProperty('--zoom-y', '0px');
      if (media) {
        media.classList.remove('is-zoomed');
      }

      if (panel) {
        panel.innerHTML = item.detailHtml || '';
      }

      if (shouldPreloadFull) {
        const fullImage = new Image();
        fullImage.onload = () => {
          if (img.dataset.lookbookLoadId !== currentLoadId) return;
          if (img.dataset.lookbookFullSrc !== fullSrc) return;
          if (img.src !== fullSrc) img.src = fullSrc;
        };
        fullImage.src = fullSrc;
      }
    };

    const handleKeydown = (event) => {
      if (!items.length) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        currentIndex = (currentIndex + 1) % items.length;
        update(currentIndex);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        currentIndex = (currentIndex - 1 + items.length) % items.length;
        update(currentIndex);
      }
    };

    const instance = window.basicLightbox.create(this.lightboxMarkup(items[currentIndex], items.length), {
      className: 'basicLightbox--img lookbook-lightbox-wrapper',
      onClose: () => {
        window.removeEventListener('keydown', handleKeydown);
      },
    });

    instance.show(() => {
      const root = instance.element().querySelector('.lookbook-lightbox');
      const media = root.querySelector('[data-lookbook-lightbox-media]');
      const image = root.querySelector('[data-lookbook-lightbox-image]');
      const prevButton = root.querySelector('[data-lookbook-lightbox-prev]');
      const nextButton = root.querySelector('[data-lookbook-lightbox-next]');
      const closeButton = root.querySelector('[data-lookbook-lightbox-close]');

      if (items.length <= 1) {
        prevButton.setAttribute('disabled', 'true');
        nextButton.setAttribute('disabled', 'true');
      }

      prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        currentIndex = (currentIndex - 1 + items.length) % items.length;
        update(currentIndex);
      });

      nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        currentIndex = (currentIndex + 1) % items.length;
        update(currentIndex);
      });

      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        instance.close();
      });

      if (image && media) {
        this.bindLightboxZoom(media, image);
      }

      update(currentIndex);
      window.addEventListener('keydown', handleKeydown);
    });
  }

  bindLightboxZoom(media, image) {
    const scale = 1.6;
    let rafId = null;

    const updateTransform = (event) => {
      if (!image.classList.contains('is-zoomed')) return;
      const rect = media.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      const maxX = (scale - 1) * rect.width * 0.5;
      const maxY = (scale - 1) * rect.height * 0.5;

      const translateX = (0.5 - x) * 2 * maxX;
      const translateY = (0.5 - y) * 2 * maxY;

      image.style.setProperty('--zoom-x', `${translateX}px`);
      image.style.setProperty('--zoom-y', `${translateY}px`);
    };

    const onMove = (event) => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateTransform(event);
      });
    };

    media.addEventListener('mousemove', onMove);

    image.addEventListener('click', (event) => {
      event.preventDefault();
      image.classList.toggle('is-zoomed');
      media.classList.toggle('is-zoomed');
      image.style.setProperty('--zoom-x', '0px');
      image.style.setProperty('--zoom-y', '0px');
    });
  }

  lightboxMarkup(item, totalItems) {
    const safeAlt = item.alt.replace(/"/g, '&quot;');
    const prevDisabled = totalItems <= 1 ? 'disabled' : '';
    const nextDisabled = totalItems <= 1 ? 'disabled' : '';
    const initialSrc = item.thumbSrc || item.src;

    return `
      <div class="lookbook-lightbox" data-lookbook-lightbox-root>
        <div class="lookbook-lightbox__content">
          <div class="lookbook-lightbox__media" data-lookbook-lightbox-media>
            <div class="lookbook-lightbox__loader" data-lookbook-lightbox-loader aria-hidden="true">
              <svg class="button__preloader-element" viewBox="25 25 50 50">
                <circle cx="50" cy="50" r="20" fill="none" stroke-width="4"></circle>
              </svg>
            </div>
            <button class="lookbook-lightbox__nav lookbook-lightbox__nav--prev" type="button" aria-label="${this.labels.prev}" data-lookbook-lightbox-prev ${prevDisabled}>
              ${this.iconMarkup('prev')}
            </button>
            <img src="${initialSrc}" alt="${safeAlt}" class="basic-lightbox-image lookbook-lightbox__image" data-lookbook-lightbox-image>
            <button class="lookbook-lightbox__nav lookbook-lightbox__nav--next" type="button" aria-label="${this.labels.next}" data-lookbook-lightbox-next ${nextDisabled}>
              ${this.iconMarkup('next')}
            </button>
          </div>
          <div class="lookbook-lightbox__panel">
            <button class="lookbook-lightbox__close" type="button" data-lookbook-lightbox-close aria-label="${this.labels.close}" title="${this.labels.close}">
              ${this.iconMarkup('close')}
            </button>
            <div class="lookbook-lightbox__panel-content" data-lookbook-lightbox-panel>
              ${item.detailHtml || ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  getLightboxThumbSrc(element) {
    const image = element.querySelector('img');
    if (!image) return '';
    return image.currentSrc || image.getAttribute('src') || '';
  }

  iconMarkup(type) {
    if (type === 'product') {
      const icon = this.section.querySelector('[data-lookbook-icon="product"]');
      return icon ? icon.innerHTML : '';
    }
    if (type === 'detail') {
      const icon = this.section.querySelector('[data-lookbook-icon="detail"]');
      return icon ? icon.innerHTML : '';
    }
    const icon = this.section.querySelector(`[data-lookbook-icon="${type}"]`);
    return icon ? icon.innerHTML : '';
  }

  bindLoadMore() {
    if (!this.loadMoreButton) return;

    this.loadMoreButton.addEventListener('click', async () => {
      const nextPage = parseInt(this.loadMoreButton.dataset.nextPage, 10);
      if (!nextPage || this.loadMoreButton.disabled) return;

      this.loadMoreButton.disabled = true;
      if (this.loadMoreLabel) {
        this.loadMoreLabel.textContent = this.loadMoreButton.dataset.labelLoading || 'Loading...';
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.sectionId);
        url.searchParams.set('page', nextPage);

        const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newItems = doc.querySelectorAll('[data-lookbook-item]');
        const newButton = doc.querySelector('[data-lookbook-load-more]');

        newItems.forEach((item) => this.grid.appendChild(item));
        this.mergeDetailMapFromDoc(doc);
        this.shuffleGrid();
        this.updateCollapseHeight();

        if (newButton) {
          const newLabel = newButton.querySelector('[data-lookbook-label]') || newButton;
          this.loadMoreButton.dataset.nextPage = newButton.dataset.nextPage;
          this.loadMoreButton.disabled = false;
          if (this.loadMoreLabel) {
            this.loadMoreLabel.textContent = newLabel.textContent;
          }
        } else {
          this.loadMoreButton.remove();
        }
      } catch (error) {
        this.loadMoreButton.disabled = false;
        if (this.loadMoreLabel) {
          this.loadMoreLabel.textContent = this.loadMoreButton.dataset.labelDefault || 'Load more';
        }
      }
    });
  }

  shuffleGrid() {
    // Disabled 2026-07-15: keep a stable, predictable card order (metaobject order).
    // Randomising reshuffled every page load and made cards jump between columns.
    return;
  }

  initCollapse() {
    if (!this.grid || !this.gridWrap || !this.collapseActions || !this.collapseToggle) return;
    if (!this.collapseEnabled || !this.collapseRows) return;

    if (this.collapseActions) {
      this.collapseActions.hidden = true;
    }

    this.collapseToggle.addEventListener('click', (event) => {
      event.preventDefault();
      const isCollapsed = this.gridWrap.classList.contains('is-collapsed');
      this.setCollapsedState(!isCollapsed);
    });

    this.grid.addEventListener('load', () => this.updateCollapseHeight(), true);
    window.addEventListener('resize', () => {
      window.clearTimeout(this.collapseResizeTimer);
      this.collapseResizeTimer = window.setTimeout(() => this.updateCollapseHeight(), 150);
    });

    this.setCollapsedState(true);

    if ('ResizeObserver' in window) {
      this.collapseObserver = new ResizeObserver(() => this.updateCollapseHeight());
      this.collapseObserver.observe(this.gridWrap);
    }
  }

  setCollapsedState(shouldCollapse) {
    if (!this.gridWrap) return;
    if (shouldCollapse) {
      this.gridWrap.classList.add('is-collapsed');
      this.gridWrap.classList.remove('is-expanded');
      this.updateCollapseHeight();
      this.setCollapseButtonText(this.collapseLabel, false);
      return;
    }
    this.gridWrap.classList.remove('is-collapsed');
    this.gridWrap.classList.add('is-expanded');
    this.gridWrap.style.removeProperty('--lookbook-collapse-height');
    this.setCollapseButtonText(this.collapseLessLabel, true);
  }

  setCollapseButtonText(label, expanded) {
    if (!this.collapseToggleText) return;
    this.collapseToggleText.textContent = label;
    this.collapseToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  updateCollapseHeight() {
    if (!this.grid || !this.gridWrap || !this.collapseEnabled || !this.collapseRows) return;
    if (!this.gridWrap.classList.contains('is-collapsed')) return;

    const items = Array.from(this.grid.children).filter((item) => item.offsetParent !== null);
    if (!items.length) return;

    const wrapHeight = this.gridWrap.clientHeight;
    const contentHeight = this.gridWrap.scrollHeight;
    if (wrapHeight && contentHeight && contentHeight <= wrapHeight + 1) {
      this.gridWrap.classList.remove('is-collapsed');
      this.gridWrap.classList.add('is-expanded');
      this.gridWrap.style.removeProperty('--lookbook-collapse-height');
      if (this.collapseActions) {
        this.collapseActions.hidden = true;
      }
      return;
    }

    const tops = items.map((item) => item.offsetTop);
    const uniqueTops = Array.from(new Set(tops)).sort((a, b) => a - b);

    if (uniqueTops.length <= this.collapseRows) {
      this.gridWrap.classList.remove('is-collapsed');
      this.gridWrap.classList.add('is-expanded');
      this.gridWrap.style.removeProperty('--lookbook-collapse-height');
      if (this.collapseActions) {
        this.collapseActions.hidden = true;
      }
      return;
    }

    const maxTop = uniqueTops[this.collapseRows - 1];
    let maxBottom = 0;
    items.forEach((item) => {
      if (item.offsetTop <= maxTop) {
        maxBottom = Math.max(maxBottom, item.offsetTop + item.offsetHeight);
      }
    });

    if (maxBottom) {
      this.gridWrap.style.setProperty('--lookbook-collapse-height', `${maxBottom}px`);
      if (this.collapseActions) {
        this.collapseActions.hidden = false;
      }
    }
  }
}

const initLookbookSections = () => {
  document.querySelectorAll('[data-lookbook-section]').forEach((section) => {
    if (!section.dataset.lookbookInitialized) {
      section.dataset.lookbookInitialized = 'true';
      new LookbookSection(section);
    }
  });
};

window.addEventListener('DOMContentLoaded', initLookbookSections);
window.addEventListener('shopify:section:load', initLookbookSections);
